import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { X, ExternalLink, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import {
  getFreeStockItem,
  formatFreeStockDetails,
  type FreeStockDetail,
  type FreeStockMovement,
} from '@/services/warehouse/freeStockApi'

interface FreeStockDetailModalProps {
  itemId: string | null
  onClose: () => void
  initialDetail?: FreeStockDetail | null
}

function formatMovementType(type: FreeStockMovement['type']): { label: string; className: string } {
  switch (type) {
    case 'RECEIPT':
      return { label: 'Nhận hàng', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' }
    case 'ISSUE':
      return { label: 'Xuất kho', className: 'bg-amber-50 text-amber-700 border-amber-200' }
    case 'CONVERT_OUT':
      return { label: 'Chuyển sang VT', className: 'bg-blue-50 text-blue-700 border-blue-200' }
    case 'RECEIPT_CORRECTION':
      return { label: 'Sửa nhận hàng', className: 'bg-purple-50 text-purple-700 border-purple-200' }
    case 'ISSUE_CORRECTION':
      return { label: 'Hủy/Sửa xuất', className: 'bg-purple-50 text-purple-700 border-purple-200' }
    case 'CONVERT_CORRECTION':
      return { label: 'Hủy chuyển VT', className: 'bg-purple-50 text-purple-700 border-purple-200' }
    default:
      return { label: type, className: 'bg-slate-50 text-slate-700 border-slate-200' }
  }
}

function formatDateTime(iso: string): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export function FreeStockDetailModal({
  itemId,
  onClose,
  initialDetail,
}: FreeStockDetailModalProps): React.ReactElement | null {
  const [data, setData] = useState<FreeStockDetail | null>(initialDetail ?? null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (initialDetail !== undefined) {
      setData(initialDetail)
      return
    }
    if (!itemId) {
      setData(null)
      setError(null)
      return
    }
    let isMounted = true
    setLoading(true)
    setError(null)

    getFreeStockItem(itemId)
      .then((res) => {
        if (isMounted) setData(res)
      })
      .catch((err) => {
        if (isMounted) setError(err.message || 'Lỗi khi tải chi tiết hàng tự do')
      })
      .finally(() => {
        if (isMounted) setLoading(false)
      })

    return () => {
      isMounted = false
    }
  }, [itemId, initialDetail])

  if (!itemId) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-xs font-sans"
      role="dialog"
      aria-modal="true"
      aria-labelledby="free-stock-detail-title"
    >
      <div className="flex max-h-[90vh] w-full max-w-4xl flex-col rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-white shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-[var(--color-border)] px-5 py-3.5">
          <div>
            <h2 id="free-stock-detail-title" className="text-base font-bold text-slate-900">
              Chi tiết hàng tự do
            </h2>
            <p className="text-xs text-slate-500">
              Lịch sử nhập, xuất, chuyển đổi và biến động tồn kho
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            aria-label="Đóng"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading && !data ? (
            <div className="flex items-center justify-center py-12 text-slate-500 text-sm">
              <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
              Đang tải chi tiết hàng tự do...
            </div>
          ) : error ? (
            <div className="rounded-[var(--radius-md)] border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
              {error}
            </div>
          ) : data ? (
            <div className="space-y-5">
              {/* Info grid */}
              <div className="grid grid-cols-2 gap-3 rounded-[var(--radius-md)] border border-slate-200 bg-slate-50 p-4 sm:grid-cols-4">
                <div>
                  <span className="text-xs text-slate-500">Tên hàng</span>
                  <div className="text-sm font-semibold text-slate-900">{data.name}</div>
                </div>
                <div>
                  <span className="text-xs text-slate-500">Đơn vị tính (ĐVT)</span>
                  <div className="text-sm font-semibold text-slate-900">{data.uom}</div>
                </div>
                <div>
                  <span className="text-xs text-slate-500">Tồn kho hiện tại</span>
                  <div className="text-base font-bold text-emerald-700 tabular-nums">
                    {data.qty_on_hand.toLocaleString('vi-VN')} {data.uom}
                  </div>
                </div>
                <div>
                  <span className="text-xs text-slate-500">Tổng đã nhận</span>
                  <div className="text-sm font-medium text-slate-700 tabular-nums">
                    {data.qty_received_total.toLocaleString('vi-VN')} {data.uom}
                  </div>
                </div>

                <div className="sm:col-span-2">
                  <span className="text-xs text-slate-500">Nguồn gốc đơn hàng (PO)</span>
                  <div className="mt-0.5">
                    <Link
                      to={`/purchasing/po/${data.po_id}`}
                      target="_blank"
                      className="inline-flex items-center gap-1 font-mono text-xs font-semibold text-[var(--color-link)] hover:underline whitespace-nowrap"
                    >
                      <span>{data.po_code || data.po_id}</span>
                      <ExternalLink className="h-3 w-3 shrink-0" />
                    </Link>
                  </div>
                </div>

                <div className="sm:col-span-2">
                  <span className="text-xs text-slate-500">Chi tiết thuộc tính mở rộng</span>
                  <div className="text-xs text-slate-800 font-medium break-words">
                    {formatFreeStockDetails(data.details)}
                  </div>
                </div>
              </div>

              {/* Movements history */}
              <div>
                <h3 className="mb-2 text-sm font-bold text-slate-900">
                  Lịch sử biến động sổ cái kho tự do ({data.movements.length})
                </h3>
                <div className="overflow-x-auto rounded-[var(--radius-md)] border border-[var(--color-border)]">
                  <table className="w-full border-collapse text-left text-xs">
                    <thead className="border-b border-[var(--color-border)] bg-slate-50 font-semibold text-slate-600">
                      <tr>
                        <th className="w-12 px-3 py-2 text-center whitespace-nowrap">STT</th>
                        <th className="w-36 min-w-[140px] px-3 py-2 whitespace-nowrap">Thời gian</th>
                        <th className="w-32 min-w-[130px] px-3 py-2 whitespace-nowrap">Loại biến động</th>
                        <th className="w-28 min-w-[120px] px-3 py-2 whitespace-nowrap">Mã chứng từ</th>
                        <th className="w-28 min-w-[100px] px-3 py-2 text-right whitespace-nowrap">Số lượng</th>
                        <th className="w-28 min-w-[110px] px-3 py-2 whitespace-nowrap">Người làm</th>
                        <th className="min-w-[160px] px-3 py-2">Ghi chú</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {data.movements.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="px-3 py-6 text-center text-slate-400">
                            Chưa có biến động kho nào
                          </td>
                        </tr>
                      ) : (
                        data.movements.map((m, idx) => {
                          const badge = formatMovementType(m.type)
                          const isPositive = m.qty_delta > 0
                          return (
                            <tr key={m.id} className="hover:bg-slate-50/80">
                              <td className="px-3 py-2 text-center text-slate-400 tabular-nums whitespace-nowrap">
                                {idx + 1}
                              </td>
                              <td className="px-3 py-2 text-slate-600 tabular-nums whitespace-nowrap">
                                {formatDateTime(m.at)}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap">
                                <span
                                  className={`inline-block whitespace-nowrap rounded border px-1.5 py-0.5 text-[11px] font-semibold ${badge.className}`}
                                >
                                  {badge.label}
                                </span>
                              </td>
                              <td className="px-3 py-2 font-mono font-medium text-slate-800 whitespace-nowrap">
                                {m.doc_code || '—'}
                              </td>
                              <td
                                className={`px-3 py-2 text-right font-semibold tabular-nums whitespace-nowrap ${
                                  isPositive ? 'text-emerald-700' : 'text-amber-800'
                                }`}
                              >
                                {isPositive ? `+${m.qty_delta.toLocaleString('vi-VN')}` : m.qty_delta.toLocaleString('vi-VN')}
                              </td>
                              <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{m.by || '—'}</td>
                              <td className="px-3 py-2 text-slate-600 break-words">{m.note || '—'}</td>
                            </tr>
                          )
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : null}
        </div>

        {/* Footer */}
        <div className="flex justify-end border-t border-[var(--color-border)] px-5 py-3">
          <Button variant="secondary" onClick={onClose}>
            Đóng
          </Button>
        </div>
      </div>
    </div>
  )
}
